import React, { useEffect, useState } from "react";
import { Bell, MessageSquare, ChevronDown, LogOut, User } from "lucide-react";
import "./horizontalnav.css";
import { getLocalStorageData } from "../../../../services/axiosComponent";

import defaultLogo from "../../../../assets/images/logo-placeholder-image.png"; // Update with your actual default logo path

const TopNavbar = () => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [logoSrc, setLogoSrc] = useState(defaultLogo);
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    try {
      // Try both possible keys for profile data
      const profileData = getLocalStorageData("profileDetails");
      console.log("Profile data:", profileData);

      if (
        profileData &&
        profileData["_ORGANIZATION_"] &&
        profileData["_ORGANIZATION_"]["_IMAGE"] &&
        profileData["_ORGANIZATION_"]["_IMAGE"] !== ""
      ) {
        const orgImage = profileData["_ORGANIZATION_"]["_IMAGE"];
        console.log(
          "Image data found, type:",
          typeof orgImage,
          "length:",
          orgImage.length
        );

        // Check if the data might already be a base64 string
        if (typeof orgImage === "string" && orgImage.startsWith("data:image")) {
          console.log("Image appears to be already in data URL format");
          setLogoSrc(orgImage);
          return;
        }

        // Try multiple approaches to convert the binary data to base64
        convertImageToBase64(orgImage);
      } else {
        console.log("No organization image data found or empty");
        setLogoSrc(defaultLogo);
      }
    } catch (error) {
      console.error("Error loading organization logo:", error);
      setLogoSrc(defaultLogo);
    }

    return () => {
      // Cleanup
    };
  }, []);

  // More robust function to handle different binary data formats
  const convertImageToBase64 = (binaryData) => {
    try {
      // Method 1: Check if data might already be base64 encoded
      if (
        typeof binaryData === "string" &&
        /^[A-Za-z0-9+/=]+$/.test(binaryData)
      ) {
        console.log("Data appears to be already base64 encoded");
        setLogoSrc(`data:image/png;base64,${binaryData}`);
        return;
      }

      // Method 2: Direct conversion for binary string
      try {
        const base64 = btoa(binaryData);
        const imgSrc = `data:image/png;base64,${base64}`;
        console.log("Method 2 successful");

        // Test if the image can be loaded before setting
        testImageValidity(imgSrc, () => {
          setLogoSrc(imgSrc);
        });
        return;
      } catch (e) {
        console.log("Method 2 failed:", e);
      }

      // Method 3: Using Uint8Array
      try {
        const bytes = new Uint8Array(binaryData.length);
        for (let i = 0; i < binaryData.length; i++) {
          bytes[i] = binaryData.charCodeAt(i);
        }

        const base64 = btoa(
          Array.from(bytes)
            .map((byte) => String.fromCharCode(byte))
            .join("")
        );

        const imgSrc = `data:image/png;base64,${base64}`;
        console.log("Method 3 successful");

        testImageValidity(imgSrc, () => {
          setLogoSrc(imgSrc);
        });
        return;
      } catch (e) {
        console.log("Method 3 failed:", e);
      }

      // If all previous methods failed, try a more aggressive approach
      try {
        // Method 4: Handle potential UTF-16 encoding issues
        let binary = "";
        const bytes = new Uint8Array(binaryData.length);
        for (let i = 0; i < binaryData.length; i++) {
          bytes[i] = binaryData.charCodeAt(i) & 0xff; // Ensure we only get the lower 8 bits
          binary += String.fromCharCode(bytes[i]);
        }

        const base64 = btoa(binary);
        const imgSrc = `data:image/png;base64,${base64}`;
        console.log("Method 4 successful");

        testImageValidity(imgSrc, () => {
          setLogoSrc(imgSrc);
        });
        return;
      } catch (e) {
        console.log("Method 4 failed:", e);
        // If all methods fail, fall back to default logo
        setLogoSrc(defaultLogo);
      }
    } catch (error) {
      console.error("Error in all image conversion methods:", error);
      setLogoSrc(defaultLogo);
    }
  };

  // Test if the generated data URL is valid before setting it
  const testImageValidity = (src, onSuccess) => {
    const img = new Image();
    img.onload = () => {
      console.log("Image loaded successfully:", src.substring(0, 50) + "...");
      onSuccess();
    };
    img.onerror = () => {
      console.error("Generated image URL is invalid");
      setLogoSrc(defaultLogo);
    };
    img.src = src;
  };

  return (
    <div
      className="admin-top-nav"
      style={{
        borderBottom: "1px solid rgba(0,0,0,.125)",
        boxShadow: "0 1px 2px rgba(0,0,0,.05)",
      }}
    >
      <div className="admin-nav-left">
        <div className="admin-logo">
          <div className="logo-container">
            <img
              src={logoSrc}
              alt="Organization Logo"
              className="admin-circular-logo"
              onError={(e) => {
                console.error("Image failed to load:", e);
                e.target.onerror = null; // Prevent infinite loop
                e.target.src = defaultLogo || "/api/placeholder/40/40"; // Fallback if default logo fails
                setLogoError(true);
              }}
            />
          </div>
          <span className="admin-logo-text">AdminDash</span>
        </div>
      </div>

      <div className="admin-nav-right">
        <button className="admin-nav-icon-btn">
          <Bell size={20} />
          <span className="admin-notification-badge">3</span>
        </button>

        <button className="admin-nav-icon-btn">
          <MessageSquare size={20} />
          <span className="admin-notification-badge">5</span>
        </button>

        <div className="admin-profile-dropdown">
          <button
            className="admin-profile-btn"
            onClick={() => setIsProfileOpen(!isProfileOpen)}
          >
            <img
              src="/api/placeholder/32/32"
              alt="Profile"
              className="admin-profile-img"
            />
            <ChevronDown size={16} />
          </button>

          {isProfileOpen && (
            <div className="admin-dropdown-menu" style={{ zIndex: 9999 }}>
              <button className="admin-dropdown-item">
                <User size={16} />
                <span>Profile</span>
              </button>
              <button
                className="admin-dropdown-item"
                onClick={() => {
                  localStorage.clear();
                  window.location.href = "/auth/sign-in";
                }}
              >
                <LogOut size={16} />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Add CSS for the circular logo */}
      <style jsx>{`
        .admin-logo {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .logo-container {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .admin-circular-logo {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          object-fit: cover;
          border: 0px solid transparent;
          transition: all 0.3s ease;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
        }

        .admin-circular-logo:hover {
          transform: scale(1.05);
          border-color: #0d6efd;
          box-shadow: 0 4px 8px rgba(13, 110, 253, 0.25);
          cursor: pointer;
        }

        .admin-logo-text {
          font-weight: 600;
          font-size: 1.2rem;
          transition: color 0.3s ease;
        }

        .admin-logo:hover .admin-logo-text {
          color: #0d6efd;
        }
      `}</style>
    </div>
  );
};

export default TopNavbar;
